import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { colors, distances, palette } from "../../styles/constants";
import Icon from "../Icons";

export interface PasswordValidation {
    minLength: boolean;
    hasNumber: boolean;
    hasSpecialChar: boolean;
    hasUppercase: boolean;
    hasLowercase: boolean;
}

export const validatePassword = (password: string): PasswordValidation => {
    return {
        minLength: password.length >= 12,
        hasNumber: /\d/.test(password),
        hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(
            password,
        ),
        hasUppercase: /[A-Z]/.test(password),
        hasLowercase: /[a-z]/.test(password),
    };
};

export const isPasswordValid = (validation: PasswordValidation): boolean => {
    return (
        validation.minLength &&
        validation.hasNumber &&
        validation.hasSpecialChar &&
        validation.hasUppercase &&
        validation.hasLowercase
    );
};

export const getMissingRequirements = (
    validation: PasswordValidation,
    t: (key: string) => string,
): string[] => {
    const missing: string[] = [];
    if (!validation.hasNumber) {
        missing.push(t("password_requirements.missing.number"));
    }
    if (!validation.hasUppercase) {
        missing.push(t("password_requirements.missing.uppercase"));
    }
    if (!validation.hasLowercase) {
        missing.push(t("password_requirements.missing.lowercase"));
    }
    if (!validation.hasSpecialChar) {
        missing.push(t("password_requirements.missing.special_char"));
    }
    if (!validation.minLength) {
        missing.push(t("password_requirements.missing.min_length"));
    }
    return missing;
};

interface PasswordRequirementsProps {
    password: string;
    showValidation?: boolean;
}

const PasswordRequirements = ({
    password,
    showValidation = false,
}: PasswordRequirementsProps) => {
    const { t } = useTranslation();

    const validation = useMemo(() => validatePassword(password), [password]);

    const requirements = [
        {
            key: "minLength",
            label: t("password_requirements.min_length"),
            met: validation.minLength,
        },
        {
            key: "hasNumber",
            label: t("password_requirements.number"),
            met: validation.hasNumber,
        },
        {
            key: "hasSpecialChar",
            label: t("password_requirements.special_char"),
            met: validation.hasSpecialChar,
        },
        {
            key: "hasUppercase",
            label: t("password_requirements.uppercase"),
            met: validation.hasUppercase,
        },
        {
            key: "hasLowercase",
            label: t("password_requirements.lowercase"),
            met: validation.hasLowercase,
        },
    ];

    return (
        <Container>
            <Title>{t("password_requirements.title")}</Title>
            <RequirementsList>
                {requirements.map((req) => (
                    <RequirementItem key={req.key}>
                        {showValidation && req.met ? (
                            <Icon
                                icon="check_circle_filled"
                                size="small"
                                color={palette.success[500]}
                            />
                        ) : (
                            <Icon
                                icon="circle_close"
                                size="small"
                                color={palette.neutral[300]}
                            />
                        )}
                        <span>{req.label}</span>
                    </RequirementItem>
                ))}
            </RequirementsList>
        </Container>
    );
};

const Container = styled.div`
    margin-bottom: ${distances.small};
`;

const Title = styled.p`
    font-size: 12px;
    line-height: 16px;
    color: ${palette.neutral[700]};
    margin: 0 0 ${distances.tiny} 0;
`;

const RequirementsList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
`;

const RequirementItem = styled.li`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    line-height: 18px;
    color: ${palette.neutral[600]};
`;

export default PasswordRequirements;
